import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Settings2, X } from 'lucide-react';
import { cn, formatIDR, truncateText } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { FilterSortType } from '@/components/sections/builder/elements/body/collection-product-grid/content';
import { BaseProductCategory } from '@/types/http/product/common';
import CardCategory from '@/components/sections/builder/elements/body/collection-product-grid/components/card-category';
import { AVAILABILITY_OPTIONS } from '@/constant/data';
import Sorting from '@/components/sections/builder/filter/sorting';
import { ProductFilterType } from '@/components/sections/builder/filter/vertical-filter';

interface DrawerFilterProps {
  data: ProductFilterType;
  categories?: BaseProductCategory[];
  filter: FilterSortType;
  price: { min: string; max: string };
  totalProducts: number;
  currentSorting: string;
  setCategory?: React.Dispatch<React.SetStateAction<string>>;
  handleCheckedChange: (type: 'inStock' | 'outStock') => void;
  handlePriceChange: (type: 'min' | 'max', value: string) => void;
  handleSelectCategory?: (id: string, name: string) => void;
  handleRemoveFilter: (name: string) => void;
  handleResetFilter: () => void;
  updateSortBy: (sortBy: string) => void;
  isLoadingCategory?: boolean;
  isFilterCategory?: boolean;
}

export default function DrawerFilter({
  data,
  categories,
  filter,
  price,
  totalProducts,
  currentSorting,
  setCategory,
  handleCheckedChange,
  handlePriceChange,
  handleSelectCategory,
  handleRemoveFilter,
  handleResetFilter,
  updateSortBy,
  isLoadingCategory,
  isFilterCategory = true,
}: DrawerFilterProps) {
  return (
    <div
      className={cn(
        'bg_product_grid_color_scheme flex justify-end space-x-2',
        data.enableFilter && 'flex'
      )}
    >
      <Sheet>
        <SheetTrigger asChild>
          <Button
            size='sm'
            variant='link'
            className='gap-2 text-base hover:underline'
            style={{ color: data.colorScheme?.text || 'black' }}
          >
            <Settings2 className='h-4 w-4' />
            Filter
          </Button>
        </SheetTrigger>
        <SheetContent className='z-[100] flex flex-col overflow-y-auto'>
          <SheetHeader className='items-center justify-center '>
            <SheetTitle>Filter</SheetTitle>
            <SheetDescription className='text-sm'>{totalProducts} Product</SheetDescription>
          </SheetHeader>
          <Separator />
          <div className='flex flex-1 flex-wrap gap-2 lg:hidden'>
            {isFilterCategory && filter.categoryId && (data.showFilterCategory ?? true) && (
              <div>
                <Button
                  variant='outline'
                  className='inline-flex items-center gap-2 rounded-full text-xs'
                  onClick={() => handleRemoveFilter('category')}
                >
                  <span>{truncateText(filter.categoryName as string, 35)}</span>
                  <X className='h-3 w-3' />
                </Button>
              </div>
            )}
            {filter.availability?.includes('1') && (data.showFilterAvailability ?? true) && (
              <div>
                <Button
                  variant='outline'
                  className='inline-flex items-center gap-2 rounded-full text-xs'
                  onClick={() => handleRemoveFilter('inStock')}
                >
                  <span>In stock</span>
                  <X className='h-3 w-3' />
                </Button>
              </div>
            )}
            {filter.availability?.includes('0') && (data.showFilterAvailability ?? true) && (
              <div>
                <Button
                  variant='outline'
                  className='inline-flex items-center gap-2 rounded-full text-xs'
                  onClick={() => handleRemoveFilter('outStock')}
                >
                  <span>Out of stock</span>
                  <X className='h-3 w-3' />
                </Button>
              </div>
            )}
            {price.min !== '' && price.max !== '' && (data.showFilterPrice ?? true) && (
              <div>
                <Button
                  variant='outline'
                  className='inline-flex items-center gap-2 rounded-full text-xs'
                  onClick={() => handleRemoveFilter('price')}
                >
                  <span>
                    {formatIDR(price.min)} - {formatIDR(price.max)}
                  </span>
                  <X className='h-3 w-3' />
                </Button>
              </div>
            )}
          </div>
          {(filter.availability?.length ||
            price.min !== '' ||
            price.max !== '' ||
            filter.categoryId) &&
            data.enableFilter && (
              <div className='lg:hidden'>
                <Button
                  variant='link'
                  className='p-0 text-xs underline'
                  onClick={handleResetFilter}
                  style={{ color: data.colorScheme?.btnSolidBackgroud }}
                >
                  Remove all
                </Button>
              </div>
            )}
          {data.enableSort && (
            <div className='flex w-full sm:hidden'>
              <Sorting updateSortBy={updateSortBy} currentSorting={currentSorting} />
            </div>
          )}
          <Separator className='md:hidden' />
          <div className='flex h-full flex-col justify-between'>
            <div className='flex flex-col space-y-3'>
              {isFilterCategory && (data.showFilterCategory ?? true) && (
                <CardCategory
                  colorScheme={data.colorScheme}
                  categories={categories as BaseProductCategory[]}
                  filter={filter}
                  isLoading={isLoadingCategory as boolean}
                  setCategory={setCategory as React.Dispatch<React.SetStateAction<string>>}
                  onSelectCategory={handleSelectCategory as (id: string, name: string) => void}
                />
              )}
              {(data.showFilterAvailability ?? true) && (
                <div className='mt-2'>
                  <h2 className='text-sm'>Availability:</h2>
                  <div className='flex flex-col space-y-3 p-3'>
                    {AVAILABILITY_OPTIONS.map(({ id, label, value }) => {
                      const isChecked = filter.availability?.includes(value);
                      const checkedStyle = isChecked
                        ? {
                            color: 'white',
                            borderColor: data.colorScheme?.btnSolidBackgroud,
                            backgroundColor: data.colorScheme?.btnSolidBackgroud,
                          }
                        : { borderColor: data.colorScheme?.btnSolidBackgroud };

                      return (
                        <Label key={id} className='flex items-center gap-2 font-normal'>
                          <Checkbox
                            id={id}
                            checked={isChecked}
                            onCheckedChange={() =>
                              handleCheckedChange(value === '1' ? 'inStock' : 'outStock')
                            }
                            style={checkedStyle}
                            isCustom
                          />{' '}
                          {label}
                        </Label>
                      );
                    })}
                  </div>
                </div>
              )}
              {(data.showFilterPrice ?? true) && (
                <div className='mt-2'>
                  <h2 className='text-sm'>Price:</h2>
                  <div className='flex w-full items-center gap-2'>
                    <Input
                      placeholder='From'
                      value={price.min}
                      onChange={(e) => handlePriceChange('min', e.target.value)}
                    />
                    <Input
                      placeholder='To'
                      value={price.max}
                      onChange={(e) => handlePriceChange('max', e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
